import { applyTheme } from './theme';
import { isEmbedded } from './embed';
import type { Theme } from './types';

export interface ThemeToggleHandle {
  destroy(): void;
}

const SUN_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round">
  <circle cx="8" cy="8" r="3"/>
  <path d="M8 1.5v1.5M8 13v1.5M1.5 8h1.5M13 8h1.5M3.2 3.2l1.1 1.1M11.7 11.7l1.1 1.1M3.2 12.8l1.1-1.1M11.7 4.3l1.1-1.1"/>
</svg>`;

const MOON_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round">
  <path d="M13 9.5A5.5 5.5 0 1 1 6.5 3 4.5 4.5 0 0 0 13 9.5Z"/>
</svg>`;

function currentTheme(): Theme {
  const v = document.documentElement.getAttribute('data-theme');
  return v === 'dark' ? 'dark' : 'light';
}

/**
 * Mounts a fixed-position theme toggle button at the top-right of the
 * screen (just to the left of the lang-picker). No-op when the demo is
 * embedded in an iframe.
 */
export function mountThemeToggle(): ThemeToggleHandle {
  if (isEmbedded()) {
    return { destroy: () => undefined };
  }

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'theme-toggle';
  btn.setAttribute('aria-label', 'Toggle theme');
  document.body.appendChild(btn);

  const render = (): void => {
    const t = currentTheme();
    // Sun icon when dark (click to go light); moon icon when light (click to go dark).
    btn.innerHTML = t === 'dark' ? SUN_SVG : MOON_SVG;
    btn.setAttribute('data-theme', t);
  };
  render();

  const onClick = (): void => {
    const next: Theme = currentTheme() === 'dark' ? 'light' : 'dark';
    applyTheme(next);
    render();
  };
  btn.addEventListener('click', onClick);

  // Also re-render if the theme is changed externally (e.g. via the demo's
  // own postMessage handler or query-string reload). Watch the attribute.
  const observer = new MutationObserver(render);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme'],
  });

  return {
    destroy(): void {
      btn.removeEventListener('click', onClick);
      observer.disconnect();
      btn.remove();
    },
  };
}
